
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Form {
  id: string;
  name: string;
}

interface FormSelectorProps {
  shopId: string;
  selectedFormId: string | null;
  onSelectForm: (formId: string | null) => void;
  defaultFormId?: string | null;
}

const FormSelector: React.FC<FormSelectorProps> = ({ 
  shopId, 
  selectedFormId, 
  onSelectForm,
  defaultFormId
}) => {
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [defaultForm, setDefaultForm] = useState<Form | null>(null);

  useEffect(() => {
    const fetchForms = async () => {
      if (!shopId) {
        setForms([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        console.log('Fetching forms for shop:', shopId);
        // Utilisation du client Supabase avec authentification
        const { data, error } = await supabase
          .from('forms')
          .select('id, name')
          .eq('shop_id', shopId);

        if (error) {
          // Si l'erreur est liée à l'authentification, afficher un message plus clair
          if (error.message.includes('JWT') || error.message.includes('auth') || error.message.includes('No API key found')) {
            console.error('Authentication error fetching forms:', error);
            toast.error('Vous devez être connecté pour gérer les formulaires', {
              description: 'Seuls les formulaires existants sont visibles aux visiteurs.'
            });
          } else {
            console.error('Error fetching forms:', error);
            toast.error('Erreur lors du chargement des formulaires');
          }
          throw error;
        }

        console.log('Forms fetched:', data?.length || 0);
        
        if (data && data.length > 0) {
          setForms(data);
          
          // Find the default form (most recent one or the one matching defaultFormId)
          if (defaultFormId) {
            const foundDefault = data.find(form => form.id === defaultFormId);
            if (foundDefault) {
              setDefaultForm(foundDefault);
            }
          } else {
            // Use the most recent form as default
            setDefaultForm(data[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch forms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, [shopId, defaultFormId]);

  // If no form is selected but a default exists, select it
  useEffect(() => {
    if (!selectedFormId && defaultForm && defaultFormId) {
      // Don't automatically select it, just show info that it will be used
      console.log('Default form will be used:', defaultForm.name);
    }
  }, [selectedFormId, defaultForm, defaultFormId, onSelectForm]);

  const handleChange = (value: string) => {
    onSelectForm(value === 'none' ? null : value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Chargement des formulaires...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Select 
        value={selectedFormId || 'none'} 
        onValueChange={handleChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Sélectionner un formulaire" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Aucun formulaire</SelectItem>
          {forms.map((form) => (
            <SelectItem key={form.id} value={form.id}>
              {form.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {!selectedFormId && defaultForm && defaultFormId && (
        <Alert variant="info" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <AlertCircle className="h-4 w-4 text-blue-500 dark:text-blue-400" />
          <AlertDescription className="text-sm text-blue-600 dark:text-blue-300">
            Le formulaire "{defaultForm.name}" sera utilisé par défaut si aucun n'est sélectionné.
          </AlertDescription>
        </Alert>
      )}
      
      {forms.length === 0 && (
        <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
          Aucun formulaire disponible pour cette boutique. Créez d'abord un formulaire dans la section Formulaires.
        </p>
      )}
    </div>
  );
};

export default FormSelector;
